import {AfterViewInit, Component, ElementRef, OnInit, QueryList, ViewChildren} from '@angular/core';
import {CarrierMetric, SubscriptionService} from '../service/subscription.service';
import {environment} from '../../environments/environment';

interface CarrierField {
  key: string;
  label: string;
  type: string;
  /** Membership in a CarrierConfig.groups section (grouped carriers only). */
  group?: string;
}

interface CarrierConfig {
  name: string;
  scac: string;
  fields: CarrierField[];
  /** Collapsible field sections (e.g. MSC's API vs Azure Service Bus).
   *  Each group saves all-or-nothing and any one complete group is enough —
   *  clients may have either connection method, or both. */
  groups?: { key: string; label: string }[];
}

/** One coloured segment of the error-composition bar (width ∝ its share of errors). */
interface ErrorSegment {
  color: string;
  grow: number;
}

/** One day's hover target on the sparkline: an invisible hit-rect plus the
 *  tooltip content and overlay-dot position for that point. */
interface SparkHit {
  /** Hit-rect geometry in viewBox units (covers the day's full-height band). */
  rectX: number;
  rectW: number;
  /** Point position as a percentage of the spark box (for the overlay dot + tooltip). */
  xPct: number;
  yPct: number;
  /** Tooltip fields. */
  dayLabel: string;
  rate: string;
  /** Polls with a definitive ok/fail outcome (success + errors); no_data excluded. */
  decided: number;
  ok: number;
  failed: number;
  attempts: number;
}

/** View-model wrapping a CarrierMetric with the geometry computed for rendering. */
interface PollingRow {
  metric: CarrierMetric;
  /** Polyline points string for the success-rate sparkline. */
  sparkPoints: string;
  /** Filled-area polygon points for the sparkline. */
  sparkArea: string;
  /** Trailing dot position as a percentage of the spark box (rendered as an HTML overlay). */
  lastXPct: number;
  lastYPct: number;
  /** Per-day hover targets + tooltip data. */
  hits: SparkHit[];
  /** The day currently hovered (drives the tooltip + hover dot), or null. */
  activeHit: SparkHit | null;
  /** Total error count over the window. */
  errorTotal: number;
  /** Bar fill width as a percentage (error rate × gain, capped at 100). */
  errorFillPct: number;
  /** Per-type error segments (only non-zero types). */
  errorSegments: ErrorSegment[];
  /** "4.2% errors" header for the error-bar tooltip. */
  errorRateLabel: string;
  /** Per-type rows (label + count + colour) for the error-bar tooltip. */
  errorBreakdown: { label: string; count: number; color: string }[];
  /** Whether the error-bar tooltip is currently shown. */
  errorsHover: boolean;
}

@Component({
    selector: 'app-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss'],
    standalone: false
})
export class SettingsComponent implements OnInit, AfterViewInit {
  activeTab: 'webhook' | 'credentials' | 'polling' = 'webhook';
  @ViewChildren('tabBtn') tabBtns!: QueryList<ElementRef<HTMLButtonElement>>;
  indicatorLeft = 0;
  indicatorWidth = 0;
  webhookUrl = '';
  webhookSecret = '';
  activated = false;
  loadingConfig = true;
  saving = false;
  activating = false;
  message = '';
  messageType: 'success' | 'danger' = 'success';

  // Carrier credentials
  carrierCredentials: Record<string, Record<string, string>> = {};
  savedCarriers: Set<string> = new Set();
  savingCredentials = false;
  credentialsMessage = '';
  credentialsMessageType: 'success' | 'danger' = 'success';

  // Health check
  healthCheckStatus: Record<string, { status: string; httpCode: number | null } | null> = {};
  healthCheckLoading: Record<string, boolean> = {};
  unsavedCarriers: Set<string> = new Set();
  showSecrets: Record<string, boolean> = {};
  clearingCarrier: Record<string, boolean> = {};
  /** Deleting credentials is destructive — require an inline confirm first. */
  confirmingDelete: Record<string, boolean> = {};
  /** Explicit open/closed state per "scac:group" key; collapsed by default. */
  expandedGroups: Record<string, boolean> = {};
  supportedCarriers = new Set(['MAEU', 'MSCU', 'CMAU', 'YMLU', 'ONEY', 'OOLU', 'COSU', 'ZIMU', 'HLCU']);

  // Polling health (dev-only feature — hidden in prod builds via environment flag)
  pollingMetricsEnabled = environment.pollingMetrics;
  pollingWindow: 7 | 30 = 7;
  pollingRows: PollingRow[] = [];
  loadingPolling = false;
  pollingError = '';
  pollingLoaded = false;
  // CMA's logo is wider than the others — render it taller, matching the credentials tab.
  readonly wideLogos = new Set(['CMAU']);
  // Hapag-Lloyd's wordmark is unusually wide — render it shorter so its footprint
  // matches the other carrier logos, matching the credentials tab.
  readonly compactLogos = new Set(['HLCU']);

  // Sparkline canvas geometry (viewBox units).
  private readonly sparkW = 300;
  private readonly sparkH = 44;
  private readonly sparkPad = 3;

  // Error colours read as a simple two-way split on the composition bar:
  //   red    = hard failures that won't fix themselves (auth, 5xx, timeout,
  //            carrier, and our own parse errors)
  //   orange = 4xx "misses" — the subscription request was rejected / didn't land
  // Grey is deliberately NOT an error colour: the bar's grey track is the
  // successful remainder, so a grey segment would read as "ok", not "error".
  // (label drives the hover tooltip's per-type breakdown.)
  // Labels are plain-English for non-technical users; HTTP codes kept in comments.
  private readonly errorTypes: { key: keyof CarrierMetric['errors']; color: string; label: string }[] = [
    {key: 'auth', color: '#dc2626', label: 'Login rejected'},            // 401/403 — bad credentials
    {key: 'client4xx', color: '#ea7317', label: 'Reference not found'},  // other 4xx — wrong ref, or subscribed too early
    {key: 'server5xx', color: '#dc2626', label: 'Carrier error'},        // 5xx — carrier server error
    {key: 'timeout', color: '#dc2626', label: 'No response'},            // carrier didn't respond in time
    {key: 'carrier', color: '#dc2626', label: 'Carrier issue'},          // carrier-reported error message
    {key: 'transform', color: '#dc2626', label: 'Processing error'},     // our parsing failed
    {key: 'invalidJson', color: '#dc2626', label: 'Unreadable response'},// carrier sent a bad payload
  ];

  carriers: CarrierConfig[] = [
    // Supported carriers
    {name: 'Maersk', scac: 'MAEU', fields: [{key: 'clientId', label: 'Client ID', type: 'text'}, {key: 'clientSecret', label: 'Client Secret', type: 'password'}]},
    {name: 'MSC', scac: 'MSCU', groups: [{key: 'api', label: 'API credentials'}, {key: 'serviceBus', label: 'Azure Service Bus'}], fields: [
      {key: 'clientId', label: 'Client ID', type: 'text', group: 'api'},
      {key: 'certificate', label: 'Certificate (Base64)', type: 'text', group: 'api'},
      {key: 'privateKey', label: 'Private Key (Base64)', type: 'password', group: 'api'},
      // Azure Service Bus queue — the connection details MSC shares at
      // Track & Trace subscription onboarding.
      {key: 'serviceBusEndpoint', label: 'Service Bus Endpoint', type: 'text', group: 'serviceBus'},
      {key: 'serviceBusQueueName', label: 'Queue Name', type: 'text', group: 'serviceBus'},
      {key: 'serviceBusSasPolicyName', label: 'Shared Access Policy', type: 'text', group: 'serviceBus'},
      {key: 'serviceBusSasKey', label: 'Shared Access Key', type: 'password', group: 'serviceBus'},
    ]},
    {name: 'CMA CGM', scac: 'CMAU', fields: [{key: 'apiKey', label: 'API Key', type: 'password'}]},
    {name: 'ONE Line', scac: 'ONEY', fields: [{key: 'username', label: 'Username', type: 'text'}, {key: 'password', label: 'Password', type: 'password'}]},
    {name: 'Yang Ming', scac: 'YMLU', fields: [{key: 'apiKey', label: 'API Key', type: 'password'}]},
    {name: 'OOCL', scac: 'OOLU', fields: [{key: 'apiKey', label: 'API Key', type: 'password'}, {key: 'clientId', label: 'Client ID', type: 'text'}]},
    {name: 'COSCO', scac: 'COSU', fields: [{key: 'apiKey', label: 'API Key', type: 'password'}, {key: 'clientId', label: 'Client ID', type: 'text'}]},
    {name: 'ZIM', scac: 'ZIMU', fields: [{key: 'subscriptionKey', label: 'Subscription Key', type: 'password'}, {key: 'clientId', label: 'Client ID', type: 'text'}, {key: 'clientSecret', label: 'Client Secret', type: 'password'}, {key: 'scope', label: 'Scope', type: 'text'}]},
    {name: 'Hapag-Lloyd', scac: 'HLCU', fields: [{key: 'clientId', label: 'Client ID', type: 'text'}, {key: 'clientSecret', label: 'Client Secret', type: 'password'}]},
    // Coming soon
    {name: 'HMM', scac: 'HDMU', fields: [{key: 'apiKey', label: 'API Key', type: 'password'}]},
    {name: 'Evergreen', scac: 'EGLV', fields: [{key: 'apiKey', label: 'API Key', type: 'password'}]},
  ];

  constructor(private subscriptionService: SubscriptionService) {
  }

  ngOnInit(): void {
    this.loadConfig();
    this.loadCarrierCredentials();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.updateIndicator());
  }

  switchTab(tab: 'webhook' | 'credentials' | 'polling'): void {
    if (tab === 'polling' && !this.pollingMetricsEnabled) {
      return;
    }
    this.activeTab = tab;
    this.updateIndicator();
    if (tab === 'polling' && !this.pollingLoaded) {
      this.loadPollingMetrics();
    }
  }

  private updateIndicator(): void {
    const idx = this.activeTab === 'webhook' ? 0 : this.activeTab === 'credentials' ? 1 : 2;
    const btn = this.tabBtns?.toArray()[idx]?.nativeElement;
    if (btn) {
      this.indicatorLeft = btn.offsetLeft;
      this.indicatorWidth = btn.offsetWidth;
    }
  }

  setPollingWindow(window: 7 | 30): void {
    if (this.pollingWindow === window) return;
    this.pollingWindow = window;
    this.loadPollingMetrics();
  }

  loadPollingMetrics(): void {
    this.loadingPolling = true;
    this.pollingError = '';
    this.subscriptionService.getCarrierMetrics(this.pollingWindow).subscribe({
      next: (res) => {
        this.pollingRows = (res.carriers || []).map(metric => this.buildPollingRow(metric));
        this.loadingPolling = false;
        this.pollingLoaded = true;
      },
      error: (err) => {
        this.loadingPolling = false;
        this.pollingLoaded = true;
        this.pollingError = err.error?.message || 'Failed to load polling metrics';
        this.pollingRows = [];
      }
    });
  }

  isWideLogo(scac: string): boolean {
    return this.wideLogos.has(scac);
  }

  isCompactLogo(scac: string): boolean {
    return this.compactLogos.has(scac);
  }

  stateLabel(state: string): string {
    return state === 'ok' ? 'Healthy' : state === 'warn' ? 'Degraded' : 'Needs attention';
  }

  /** Compute the inline-SVG sparkline geometry + error-bar segments for one carrier. */
  private buildPollingRow(metric: CarrierMetric): PollingRow {
    const {sparkW: W, sparkH: H, sparkPad: pad} = this;
    const series = metric.series || [];
    const n = series.length;

    // Per-day success rate, defined exactly like the backend aggregate:
    // success / (success + errors). no_data polls are neutral and excluded, so a
    // day that only returned "no data" reads as 100% rather than crashing to 0 —
    // this keeps the sparkline consistent with the carrier's headline state
    // (otherwise a single no_data poll draws a cliff under an "ok / 100%" badge).
    const rates = series.map(d => {
      const decided = d.success + d.errors;
      return decided ? (d.success / decided) * 100 : 100;
    });
    // Float the floor up to ~55% so the line uses the vertical space, like the mockup.
    const lo = rates.length ? Math.min(55, Math.floor(Math.min(...rates) - 6)) : 0;
    const hi = 100;
    const span = hi - lo || 1;
    const x = (i: number) => n > 1 ? pad + (i * (W - 2 * pad)) / (n - 1) : W / 2;
    const y = (r: number) => pad + (H - 2 * pad) * (1 - (r - lo) / span);

    const pts = rates.map((r, i) => `${x(i).toFixed(1)},${y(r).toFixed(1)}`);
    const sparkPoints = pts.join(' ');
    const sparkArea = `${pad},${H} ${sparkPoints} ${W - pad},${H}`;
    const lastIdx = n - 1;
    const lastX = n ? x(lastIdx) : W / 2;
    const lastY = n ? y(rates[lastIdx]) : H / 2;
    const lastXPct = (lastX / W) * 100;
    const lastYPct = (lastY / H) * 100;

    // One hover target per day: a full-height band carrying that day's tooltip.
    // Positions are stored as percentages of the spark box so the round HTML
    // overlay dots/tooltip line up with the (horizontally stretched) SVG.
    const hits: SparkHit[] = series.map((d, i) => {
      const cx = x(i), cy = y(rates[i]);
      const off = (n - 1) - i;
      const dayLabel = off === 0 ? 'today' : off === 1 ? 'yesterday' : `${off}d ago`;
      return {
        rectX: n > 1 ? cx - (W / n) / 2 : 0,
        rectW: n > 1 ? W / n : W,
        xPct: (cx / W) * 100,
        yPct: (cy / H) * 100,
        dayLabel,
        rate: rates[i].toFixed(1),
        decided: d.success + d.errors,
        ok: d.success,
        failed: d.errors,
        attempts: d.attempts,
      };
    });

    // Errors: total count + composition bar whose fill ∝ error rate (×2 gain, capped 100%).
    const errors = metric.errors;
    const errorTotal = this.errorTypes.reduce((a, t) => a + (errors[t.key] || 0), 0);
    const errorRate = metric.attempts ? errorTotal / metric.attempts : 0;
    const errorFillPct = Math.min(100, errorRate * 200);
    const present = this.errorTypes.filter(t => (errors[t.key] || 0) > 0);
    const errorSegments: ErrorSegment[] = present.map(t => ({color: t.color, grow: errors[t.key]}));
    // Styled-tooltip breakdown for the bar: a header rate + one row per type.
    const errorRateLabel = `${(errorRate * 100).toFixed(1)}% errors`;
    const errorBreakdown = present.map(t => ({label: t.label, count: errors[t.key], color: t.color}));

    return {
      metric, sparkPoints, sparkArea, lastXPct, lastYPct, hits, activeHit: null,
      errorTotal, errorFillPct, errorSegments, errorRateLabel, errorBreakdown, errorsHover: false,
    };
  }

  loadConfig(): void {
    this.loadingConfig = true;
    this.subscriptionService.getWebhookConfig().subscribe({
      next: (config: any) => {
        this.webhookUrl = config.targetUrl || '';
        this.activated = config.activated || false;
        this.loadingConfig = false;
      },
      error: () => {
        this.loadingConfig = false;
      }
    });
  }

  loadCarrierCredentials(): void {
    this.subscriptionService.getCarrierCredentials().subscribe({
      next: (res) => {
        this.carrierCredentials = res.carrierCredentials || {};
        this.savedCarriers = new Set(Object.keys(this.carrierCredentials).filter(
          scac => Object.values(this.carrierCredentials[scac]).some(v => !!v)
        ));
      },
      error: () => {
      }
    });
  }

  getCredentialValue(scac: string, field: string): string {
    return this.carrierCredentials[scac]?.[field] || '';
  }

  groupFields(carrier: CarrierConfig, groupKey: string): CarrierField[] {
    return carrier.fields.filter(f => f.group === groupKey);
  }

  isGroupOpen(carrier: CarrierConfig, groupKey: string): boolean {
    // Collapsed by default — sections only open on an explicit click.
    return this.expandedGroups[`${carrier.scac}:${groupKey}`] ?? false;
  }

  toggleGroup(carrier: CarrierConfig, groupKey: string): void {
    this.expandedGroups[`${carrier.scac}:${groupKey}`] = !this.isGroupOpen(carrier, groupKey);
  }

  setCredentialValue(scac: string, field: string, value: string): void {
    if (!this.carrierCredentials[scac]) {
      this.carrierCredentials[scac] = {};
    }
    this.carrierCredentials[scac][field] = value;
    this.unsavedCarriers.add(scac);
  }

  hasCredentials(scac: string): boolean {
    return this.savedCarriers.has(scac);
  }

  saveCarrierCredentials(): void {
    const toSave: Record<string, Record<string, string>> = {};
    for (const carrier of this.carriers) {
      const creds = this.carrierCredentials[carrier.scac];
      if (!creds) {
        continue;
      }
      if (carrier.groups) {
        // Grouped carrier: each section is all-or-nothing, any one complete
        // section is enough to save.
        let completeGroups = 0;
        for (const group of carrier.groups) {
          const fields = this.groupFields(carrier, group.key);
          const filled = fields.filter(f => creds[f.key]?.trim());
          if (filled.length && filled.length !== fields.length) {
            this.credentialsMessage = `${carrier.scac}: the "${group.label}" fields must be filled in together or left empty`;
            this.credentialsMessageType = 'danger';
            return;
          }
          if (filled.length) {
            completeGroups++;
          }
        }
        if (completeGroups > 0) {
          // Omit empty sections — the backend replaces the whole carrier
          // object, so this is also how a section gets cleared.
          toSave[carrier.scac] = Object.fromEntries(
            carrier.fields.filter(f => creds[f.key]?.trim()).map(f => [f.key, creds[f.key]])
          );
        }
      } else {
        const hasAllFields = carrier.fields.every(f => creds[f.key]?.trim());
        if (hasAllFields) {
          toSave[carrier.scac] = creds;
        }
      }
    }
    if (Object.keys(toSave).length === 0) {
      this.credentialsMessage = 'No credential changes to save';
      this.credentialsMessageType = 'danger';
      return;
    }
    this.savingCredentials = true;
    this.credentialsMessage = '';
    this.subscriptionService.saveCarrierCredentials(toSave).subscribe({
      next: () => {
        this.savingCredentials = false;
        this.credentialsMessage = 'Carrier credentials saved';
        this.credentialsMessageType = 'success';
        Object.keys(toSave).forEach(scac => {
          this.savedCarriers.add(scac);
          this.unsavedCarriers.delete(scac);
        });
      },
      error: (err) => {
        this.savingCredentials = false;
        this.credentialsMessage = err.error?.message || 'Failed to save credentials';
        this.credentialsMessageType = 'danger';
      }
    });
  }

  saveWebhook(): void {
    if (!this.webhookUrl.trim()) return;
    this.saving = true;
    this.message = '';
    this.subscriptionService.configureWebhook(this.webhookUrl.trim()).subscribe({
      next: (res) => {
        this.saving = false;
        this.activated = false;
        this.message = res.message || 'Webhook configured — a secret has been sent to your URL';
        this.messageType = 'success';
      },
      error: (err) => {
        this.saving = false;
        this.message = err.error?.message || 'Failed to configure webhook';
        this.messageType = 'danger';
      }
    });
  }

  activateWebhook(): void {
    if (!this.webhookSecret.trim()) return;
    this.activating = true;
    this.message = '';
    this.subscriptionService.activateWebhook(this.webhookSecret.trim()).subscribe({
      next: (res) => {
        this.activating = false;
        this.activated = true;
        this.message = res.message || 'Webhook activated';
        this.messageType = 'success';
        this.webhookSecret = '';
      },
      error: (err) => {
        this.activating = false;
        this.message = err.error?.message || 'Failed to activate webhook';
        this.messageType = 'danger';
      }
    });
  }

  confirmClearCredentials(scac: string): void {
    this.confirmingDelete[scac] = false;
    this.clearCarrierCredentials(scac);
  }

  clearCarrierCredentials(scac: string): void {
    this.clearingCarrier[scac] = true;
    this.subscriptionService.deleteCarrierCredentials([scac]).subscribe({
      next: () => {
        this.clearingCarrier[scac] = false;
        this.savedCarriers.delete(scac);
        delete this.carrierCredentials[scac];
        this.healthCheckStatus[scac] = null;
        this.credentialsMessage = `${scac} credentials cleared`;
        this.credentialsMessageType = 'success';
      },
      error: (err) => {
        this.clearingCarrier[scac] = false;
        this.credentialsMessage = err.error?.message || `Failed to clear ${scac} credentials`;
        this.credentialsMessageType = 'danger';
      }
    });
  }

  /**
   * Human label for a health-check result. Only a genuine auth rejection reads
   * as "Login failed"; a successful check reads "Connected"; anything else
   * (transient HTTP errors, timeouts, no creds) shows the real status so a
   * working-but-empty response isn't mislabelled as bad credentials.
   */
  healthLabel(scac: string): string {
    const status = this.healthCheckStatus[scac]?.status;
    if (!status) return '';
    if (status === 'OK') return 'Connected';
    if (status === 'AUTH FAILED') return 'Login failed';
    return status;
  }

  runHealthCheck(scac: string): void {
    this.healthCheckLoading[scac] = true;
    this.healthCheckStatus[scac] = null;
    this.subscriptionService.carrierHealthCheck(scac).subscribe({
      next: (res) => {
        this.healthCheckLoading[scac] = false;
        this.healthCheckStatus[scac] = {status: res.status, httpCode: res.httpCode};
      },
      error: (err) => {
        this.healthCheckLoading[scac] = false;
        this.healthCheckStatus[scac] = {status: err.error?.message || 'ERROR', httpCode: null};
      }
    });
  }
}
